import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react"
import { defaultAdSpendAllocations } from "../../consts/forecast";

export const AdSpendAllocationForecast = ({
    allocationsData,
    access,
    forecastType,
    handleOnChangeYear,
    handleOnChangeMonth,
    handleApplyAllMonths
}) => {
    const [allocations, setAllocations] = useState(allocationsData || defaultAdSpendAllocations)

    useEffect(() => {
        if (allocationsData) {
            setAllocations(allocationsData);
        }
    }, [allocationsData]);

    useEffect(() => {
        if (forecastType === "month") handleOnChangeMonth?.(allocations);
        if (forecastType === "year") handleOnChangeYear?.(allocations);
    }, [allocations, forecastType]);

    const handleNameChange = (index, value) => {
        const newAllocations = [...allocations];
        newAllocations[index] = { ...newAllocations[index], name: value };
        setAllocations(newAllocations);
    };

    const handlePercentageChange = (index, value) => {
        const newValue = Math.min(100, Math.max(0, Number(value) || 0));
        const newAllocations = [...allocations];
        newAllocations[index] = { ...newAllocations[index], percentage: newValue };
        setAllocations(newAllocations);
    };

    const handleDelete = (index) => {
        const newAllocations = allocations.filter((_, i) => i !== index);
        setAllocations(newAllocations);
    };

    const handleAddChannel = () => {
        const newId = Math.max(...allocations.map(a => a.id), 0) + 1;
        setAllocations([...allocations, { id: newId, name: "", percentage: 0 }]);
    };

    const handleResetToDefaults = () => {
        setAllocations(defaultAdSpendAllocations);
    };

    const totalPercentage = allocations.reduce((sum, allocation) => sum + (allocation.percentage || 0), 0);

    return (
        <div className="rounded-lg border bg-card text-card-foreground shadow-2xs">
            <div className="flex flex-col space-y-1.5 p-6">
                <h3 className="text-2xl font-semibold leading-none tracking-tight margin-0">
                    Ad Spend Allocation by Channel
                </h3>
            </div>
            <div className="p-6 pt-0 space-y-6">
                <div className="overflow-x-auto">
                    <div className="relative w-full overflow-auto">
                        <table className="w-full caption-bottom text-sm">
                            <thead>
                                <tr className="border-b transition-colors hover:bg-gray-100">
                                    <th className="h-12 px-4 align-middle font-medium text-muted-foreground text-left">
                                        Channel Name
                                    </th>
                                    <th className="h-12 px-4 align-middle font-medium text-muted-foreground text-right">
                                        % of Ad Spend
                                    </th>
                                    {access === "edit" && (
                                        <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 w-16" />
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {(allocations || []).map((allocation, index) => (
                                    <tr
                                        className="border-b transition-colors hover:bg-gray-50"
                                        key={`${allocation.id}_${index}`}
                                    >
                                        <td className="p-4 align-middle">
                                            <input
                                                value={allocation.name}
                                                disabled={access !== "edit"}
                                                onChange={(e) => {
                                                    if (access === "edit") {
                                                        handleNameChange(index, e.target.value)
                                                    }
                                                }}
                                                className="flex h-10 w-full rounded-md border-input bg-background text-sm ring-offset-background placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 border-0 p-0"
                                            />
                                        </td>
                                        <td className="p-4 align-middle">
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                value={allocation.percentage}
                                                disabled={access !== "edit"}
                                                onChange={(e) => {
                                                    if (access === "edit") {
                                                        handlePercentageChange(index, e.target.value)
                                                    }
                                                }}
                                                className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 text-right w-20 ml-auto"
                                            />
                                        </td>
                                        {access === "edit" && (
                                            <td className="p-4 align-middle">
                                                <button
                                                    onClick={() => handleDelete(index)}
                                                    className="ring-offset-background inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 rounded-md h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                                >
                                                    <XMarkIcon className="h-5 w-5 text-red-700" />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="flex items-center justify-between">
                    {access === "edit" ? (
                        <button
                            onClick={handleAddChannel}
                            className="ring-offset-background justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors border-input border h-10 px-4 py-2 flex items-center gap-2 bg-transparent hover:bg-gray-100"
                        >
                            + Add Channel
                        </button>
                    ) : (
                        <div />
                    )}
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Total:</span>
                        <div className="flex items-center gap-1">
                            {totalPercentage === 100 && (
                                <CheckIcon className="h-4 w-4 text-green-600" />
                            )}
                            {totalPercentage !== 100 && (
                                <XMarkIcon className="h-4 w-4 text-red-600" />
                            )}
                            <span className={`text-sm font-bold ${totalPercentage === 100 ? 'text-green-600' : 'text-red-600'}`}>
                                {totalPercentage}%
                            </span>
                        </div>
                    </div>
                </div>
                {(totalPercentage < 100 || totalPercentage > 100) && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                        <div className="flex items-center gap-2 text-red-700">
                            <span className="text-sm font-medium">
                                {totalPercentage < 100 ? "Under allocated" : "Over allocated"} by {totalPercentage}%
                            </span>
                        </div>
                    </div>
                )}
                {access === "edit" && (
                    <div className="flex gap-3">
                        {forecastType === "month" && (
                            <button 
                                onClick={() => {
                                    handleApplyAllMonths?.(allocations);
                                }}
                                className="ring-offset-background inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors border-input bg-background hover:bg-gray-100 border h-10 px-4 py-2"
                            >
                                Apply to All Months
                            </button>
                        )}
                        <button 
                            onClick={handleResetToDefaults}
                            className="ring-offset-background inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors border-input bg-background hover:bg-gray-100 border h-10 px-4 py-2"
                        >
                            Reset to Defaults
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}